import { Component, inject } from '@angular/core';
import { Location } from '@angular/common';
import { ColDef, ValueFormatterParams } from 'ag-grid-community';
import { NgbModal, NgbModalOptions } from '@ng-bootstrap/ng-bootstrap';
import { of, pipe, switchMap } from 'rxjs';
import { DeleteEventDataModel } from '@app/shared/components/simple-grid/simple-grid.model';
import { RentalAgreementsService } from './rental-agreements.service';
import { RentalAgreementsUpsertDialogComponent } from './rental-agreements-upsert-dialog/rental-agreements-upsert-dialog.component';
import { ApiPaginatedGridComponent } from '@app/shared/components/api-paginated-grid/api-paginated-grid.component';
import { GridDataRequest } from '@app/shared/components/api-paginated-grid/api-paginated-grid.model';
import { RentalAgreementFilter } from './rental-agreements.model';
import { ActivatedRoute, Router } from '@angular/router';
import { RentalAgreementFilterComponent } from './rental-agreement-filter/rental-agreement-filter.component';
import { RaStatusRendererComponent } from '@app/shared/components/simple-grid/ra-status-renderer/ra-status-renderer.component';

@Component({
  selector: 'app-rental-agreements',
  standalone: true,
  imports: [ApiPaginatedGridComponent, RentalAgreementFilterComponent],
  templateUrl: './rental-agreements.component.html',
  host: { class: 'd-flex flex-fill' }
})
export class RentalAgreementsComponent {
  private _modalService = inject(NgbModal);
  private _rentalAgreementsService = inject(RentalAgreementsService);
  private _router = inject(Router);
  private _route = inject(ActivatedRoute);
  private _location = inject(Location);
  private _filter: RentalAgreementFilter = {};
  initialFilterState: RentalAgreementFilter | null = null;

  listRentalAgreementsOperator = pipe(
    switchMap((dataRequest: GridDataRequest) =>
      this._rentalAgreementsService.getAllRentalAgreements({ pageInfo: dataRequest.pageInfo, filter: this._filter })
    )
  );

  createRentalAgreementOperator = pipe(
    switchMap(() => {
      const options: NgbModalOptions = {
        fullscreen: 'xxl',
        scrollable: true,
        size: 'xl',
        centered: true,
        modalDialogClass: 'align-items-stretch'
      };
      const modalRef = this._modalService.open(RentalAgreementsUpsertDialogComponent, options);
      modalRef.componentInstance.config.set({ mode: 'create' });
      return modalRef.closed;
    })
  );

  updateRentalAgreementOperator = pipe(
    switchMap((uid: string) => {
      this._router.navigate([], {
        queryParams: { selected: uid },
        queryParamsHandling: 'merge'
      });
      return of(void 0);
    })
  );

  deleteRentalAgreementOperator = pipe(
    switchMap((data: DeleteEventDataModel) => this._rentalAgreementsService.deleteRentalAgreement(data.uid))
  );

  columnDefs: ColDef[] = [
    {
      field: 'number',
      minWidth: 80,
      maxWidth: 80,
      pinned: 'left'
    },
    {
      field: 'reservationNumber',
      minWidth: 80,
      maxWidth: 80
    },
    {
      field: 'referenceNumber',
      headerName: 'Ref. Number',
      minWidth: 100
    },
    {
      field: 'status',
      cellRenderer: RaStatusRendererComponent,
      minWidth: 110
    },
    {
      field: 'driverFirstName',
      minWidth: 120
    },
    {
      field: 'driverLastName',
      minWidth: 120
    },
    {
      field: 'locationOut',
      minWidth: 100
    },
    {
      field: 'locationIn',
      minWidth: 100
    },
    {
      field: 'dateOut',
      valueFormatter: (params: ValueFormatterParams) => new Date(params.value).toLocaleString(),
      minWidth: 90,
      tooltipValueGetter: (params) => new Date(params.value).toLocaleString()
    },
    {
      field: 'dateIn',
      valueFormatter: (params: ValueFormatterParams) => new Date(params.value).toLocaleString(),
      minWidth: 90,
      tooltipValueGetter: (params) => new Date(params.value).toLocaleString()
    },
    {
      field: 'clientNumber',
      headerName: 'Broker Nr.',
      minWidth: 80
    },
    {
      field: 'createdDate',
      valueFormatter: (params: ValueFormatterParams) => new Date(params.value).toLocaleString(),
      minWidth: 90
    }
  ];

  constructor() {
    this._route.queryParams.subscribe((x) => {
      const { selected, selectedVersion, ...filter } = x;
      this._filter = filter;
      if (Object.keys(filter).length !== 0) {
        this.initialFilterState = filter;
      }
      this._modalService.dismissAll('quite');
      if (selected) {
        const options: NgbModalOptions = {
          fullscreen: 'xxl',
          scrollable: true,
          size: 'xl',
          centered: true,
          modalDialogClass: 'align-items-stretch'
        };
        const modalRef = this._modalService.open(RentalAgreementsUpsertDialogComponent, options);

        const config =
          selectedVersion && !isNaN(Number(selectedVersion))
            ? { mode: 'readonly', uid: selected, version: Number(selectedVersion) }
            : { mode: 'readonly', uid: selected };

        modalRef.componentInstance.config.set(config);

        modalRef.dismissed
          .pipe(
            switchMap((x) => {
              if (x === 'quite') {
                return of(void 0);
              }
              this._location.back();
              return of(void 0);
            })
          )
          .subscribe();
      }
    });
  }

  filterChanged(newFilter: RentalAgreementFilter): void {
    this._router.navigate(['/operations/rental-agreements'], { queryParams: newFilter });
  }
}
